import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Lock } from 'lucide-react';
import { config } from '@/config';
import AnimatedBackground from '@/components/AnimatedBackground';
import OneTimePasswordModal from '@/pages/login/OneTimePasswordModal';
import { setSession } from '@/redux/actions/sessionAction';
import { useDispatch } from 'react-redux';
import { setCustomer } from '@/redux/actions/customerAction';
import { clearProgram } from '@/redux/actions/programAction';
const LogInPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [openOneTimePasswordModal, setOpenOneTimePasswordModal] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setErrorMessage('')
            const response = await axios.post(`${config.backendUrl}${config.loginUrl}`, { email, password });
            console.log(response.data);
            if (response.data.nextStep === 'oneTimePassword') {
                setOpenOneTimePasswordModal(true);
            } else {
                const { sessionToken, email: responseEmail, _id } = response.data;
                const session = { sessionToken, email: responseEmail || email, _id: _id || null };
                dispatch(setSession(session));
                dispatch(clearProgram());
                dispatch(setCustomer(response.data.customer || { cardCode: null, name: null }));
                navigate('/select-program');
            }
        } catch (error: any) {
            console.error('There was an error!', error);
            setErrorMessage(error.response?.data?.message || 'Unknown error');
        }
    };

    const handleCloseModal = () => {
        setOpenOneTimePasswordModal(false);
    };

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
            <AnimatedBackground />
            <OneTimePasswordModal
                isOpen={openOneTimePasswordModal}
                onClose={handleCloseModal}
                email={email}
            />
            <div className="max-w-md w-full space-y-8 p-10 rounded-xl bg-white/90 backdrop-blur-sm shadow-xl transform transition-all duration-300 hover:shadow-2xl">
                <div className="text-center">
                    <img src="/lionsfloor.png" className="w-20 h-20 mx-auto" alt="Lions Floor" />
                    <h2 className="mt-6 text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-800 to-amber-500">
                        Welcome to Foundation Elite
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Your premium flooring rewards portal
                    </p>
                </div>
                <form className="mt-8 space-y-6" onSubmit={handleLogin}>
                    <div className="rounded-md space-y-4">
                        <div className="group">
                            <div className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 group-focus-within:border-slate-800 group-focus-within:ring-1 group-focus-within:ring-amber-400 transition-all duration-200">
                                <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-slate-800 transition-colors" />
                                <input
                                    id="email-address"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    className="flex-1 outline-none text-gray-900 placeholder-gray-500"
                                    placeholder="Email address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="group">
                            <div className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 group-focus-within:border-slate-800 group-focus-within:ring-1 group-focus-within:ring-amber-400 transition-all duration-200">
                                <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-slate-800 transition-colors" />
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    className="flex-1 outline-none text-gray-900 placeholder-gray-500"
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="flex justify-between">
                            <button
                                type="button"
                                onClick={() => navigate('/forgot-password')}
                                className="text-sm text-slate-800 hover:text-amber-500 font-medium"
                            >
                                Forgot your password?
                            </button>
                        </div>
                    </div>

                    {errorMessage && (
                        <div className="text-center text-red-500 text-sm animate-shake">
                            {errorMessage}
                        </div>
                    )}

                    <div>
                        <button
                            type="submit"
                            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-slate-800 to-slate-700 hover:from-slate-900 hover:to-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-400 transition-all duration-200 transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
                        >
                            <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                <Lock className="h-5 w-5 text-amber-400 group-hover:text-amber-300 transition-colors" />
                            </span>
                            Access Foundation Elite
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LogInPage;
